import axios, {AxiosRequestConfig} from 'axios'
import {Dispatch, SetStateAction, useCallback, useEffect, useState} from 'react'
import {JsonData} from 'utilities/types'

const defualtHeaders: HeadersInit = {
  accept: 'application/json',
}

export interface GetDataResponse<TData = unknown> {
  loading: boolean
  error: Error | undefined
  data: TData | undefined
  setData: Dispatch<SetStateAction<TData | undefined>>
}

export interface GetDataOptions<TResponse = unknown> {
  onComplete?: (res: TResponse) => void
  onError?: (e: any) => void
  excludeAuthHeaders?: boolean
  loading?: boolean
  method?: 'get' | 'delete' | 'post'
}

export async function getData<TResponse = unknown>(
  path: string,
  method: 'get' | 'delete' | 'post' = 'get',
  axios_tenant?: string | AxiosRequestConfig
): Promise<TResponse> {
  if (axios_tenant) {
    const config =
      typeof axios_tenant === 'string'
        ? {
            headers: {
              'x-tenant-unique-id': axios_tenant,
            },
          }
        : axios_tenant
    const res = await axios.get(path, config)
    return res.data as unknown as TResponse & JsonData
  } else {
    switch (method) {
      case 'get': {
        const res = await axios.get(encodeURI(path))
        return res.data as unknown as TResponse & JsonData
      }
      case 'delete': {
        const res = await axios.delete(path)
        return res.data as unknown as TResponse & JsonData
      }
      case 'post': {
        const res = await axios.post(path)
        return res.data as unknown as TResponse & JsonData
      }
    }
  }
}

const useGetData = <TResponse = unknown>(
  path: string,
  {
    lazyLoad = false,
  }: {
    lazyLoad?: boolean
  }
): [(options?: GetDataOptions<TResponse>) => Promise<void>, GetDataResponse<TResponse>] => {
  const [data, setData] = useState<TResponse | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | undefined>()

  const execGetData = useCallback(
    async (options?: GetDataOptions<TResponse>) => {
      setLoading(true)

      try {
        getData(path, options && options.method ? options.method : 'get')
          .then((response) => {
            setData(response as TResponse)
            setLoading(false)
            options?.onComplete?.(response as TResponse)
          })
          .catch((e) => {
            setData(undefined)
            setLoading(false)
            setError(e)
            options?.onError?.(e)
          })
      } catch (e) {
        setError(e as Error)
        setLoading(false)
        options?.onError?.(e)
      }
    },
    [path]
  )

  useEffect(() => {
    if (lazyLoad) {
      return
    }
    execGetData()
  }, [lazyLoad, execGetData])

  return [execGetData, {data, loading, error, setData}]
}
